import React, { useCallback, useLayoutEffect, useRef } from 'react';
import { getCaretOffset, setCaretOffset } from '../../utils/caretRestore';
import { readSubscripts, renderSubscripts, stripSubscripts, writeSubscripts } from '../../subscript';

/**
 * A plain-text contentEditable that commits what is typed, not what is blurred.
 *
 * Every short text field in a document (a table cell, a caption, a list item,
 * an appendix title) used to hand its text to the editor only on blur. Text
 * still sitting in the focused element was invisible to the autosave, the
 * save indicator, the unmount flush and the beforeunload guard -- and Chrome
 * fires no blur when React removes a focused element, so Escape, Back, a
 * reload or a repagination that moved the row to another page threw the
 * text away with no save attempted. Reproduced on a table cell before this
 * existed: "Saved" on the toolbar, Escape, gone.
 *
 * So this does what ParagraphBlock does for the letter body:
 *
 * - **Commits shortly after each keystroke** (COMMIT_DELAY_MS), again on
 *   blur, and again in a layout-effect cleanup on unmount, so no DOM text can
 *   outlive its element uncommitted.
 * - **Owns its DOM while editable.** React never renders children into an
 *   editable instance: a committed value that differs from the previous prop
 *   would make React rewrite the text node, and the DOM "replace data" rule
 *   sends every caret inside that node to offset 0. The text is written with
 *   `textContent` from a layout effect instead, only when it changed
 *   externally (undo, a refresh, a row turned into a heading). Rendering the
 *   value as children is only done when not editable, and the element is
 *   keyed on `editable` so the two modes never share a DOM node.
 * - **Carries the caret across a remount.** A field that unmounts while
 *   focused (its row moved to the next page; the page above it grew) stashes
 *   its text and caret under `restoreKey`; the instance that mounts with the
 *   same key within two seconds takes focus back at the same character.
 *
 * **Subscripts** are the one markup a field holds: `M_{d}` in the value is a
 * `<sub>` in the DOM, written by `writeSubscripts` and read back by the default
 * `read`, so the field shows the subscript while it is edited and stores
 * plain text (subscript.ts). Every write below goes through it, never through
 * textContent, which would flatten a subscript into the text around it.
 *
 * `read` turns the DOM into the committed text (default: the text, with
 * subscripts as markup). The
 * invariant every caller must keep: `read` of a DOM holding `value` returns
 * `value`, or the field commits a correction on mount and, worse, a label
 * rendered into the editable text is committed as data. Render labels as
 * sibling elements, never inside the editable one.
 */

export const COMMIT_DELAY_MS = 120;

interface CaretStash {
  key: string;
  text: string;
  offset: number;
  createdAt: number;
}

const STASH_MAX_AGE_MS = 2000;
let stash: CaretStash | null = null;

const takeStash = (key: string): CaretStash | null => {
  if (!stash || stash.key !== key) return null;
  const record = stash;
  stash = null;
  return Date.now() - record.createdAt <= STASH_MAX_AGE_MS ? record : null;
};

type Tag = 'span' | 'div' | 'p' | 'h1' | 'h2' | 'h3';

export interface LiveTextProps extends Omit<React.HTMLAttributes<HTMLElement>, 'children' | 'onInput'> {
  as?: Tag;
  /** The stored text. */
  value: string;
  /** Called with the text to store; never with a value equal to `value`. */
  onCommit: (text: string) => void;
  editable: boolean;
  /** DOM -> committed text. Default: textContent. */
  read?: (el: HTMLElement) => string;
  /**
   * On blur, put the DOM back to exactly `read(el)`. For a `read` that drops
   * what the browser left behind (a caret holder, a trailing newline), so the
   * element measures as the text it stores. Off by default: a `read` that
   * strips a label would otherwise strip it from the screen.
   */
  settleOnBlur?: boolean;
  /** Identifies this field across a remount, for the caret stash. */
  restoreKey?: string;
  /** The element, for a caller that needs to focus or measure it. */
  elementRef?: React.Ref<HTMLElement>;
}

const defaultRead = readSubscripts;

const LiveText: React.FC<LiveTextProps> = ({
  as = 'span',
  value: rawValue,
  onCommit,
  editable,
  read = defaultRead,
  settleOnBlur = false,
  restoreKey,
  elementRef,
  onFocus,
  onBlur,
  ...rest
}) => {
  // Older rows can lack an optional field (a caption never set); undefined
  // here would make every blur commit '' as an edit.
  const value = rawValue ?? '';
  const elRef = useRef<HTMLElement | null>(null);
  // Latest props for timers and the unmount cleanup, which must not close
  // over a stale render.
  const valueRef = useRef(value);
  const onCommitRef = useRef(onCommit);
  const readRef = useRef(read);
  const restoreKeyRef = useRef(restoreKey);
  valueRef.current = value;
  onCommitRef.current = onCommit;
  readRef.current = read;
  restoreKeyRef.current = restoreKey;

  // The text last written into the DOM or last committed from it. The value
  // prop equalling this means the DOM already shows it.
  const lastSetRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const focusedRef = useRef(false);

  const setEl = useCallback((node: HTMLElement | null) => {
    elRef.current = node;
    if (typeof elementRef === 'function') elementRef(node);
    else if (elementRef) (elementRef as React.MutableRefObject<HTMLElement | null>).current = node;
  }, [elementRef]);

  const commit = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const el = elRef.current;
    if (!el) return;
    const text = readRef.current(el);
    lastSetRef.current = text;
    if (text !== valueRef.current) onCommitRef.current(text);
  }, []);

  // Mount: write the value, or take over a stashed caret. Unmount: commit
  // anything still waiting, and stash the caret if this field had it.
  useLayoutEffect(() => {
    if (!editable) return;
    const el = elRef.current;
    if (!el) return;

    const restored = restoreKeyRef.current ? takeStash(restoreKeyRef.current) : null;
    if (restored) {
      writeSubscripts(el, restored.text);
      lastSetRef.current = restored.text;
      el.focus();
      focusedRef.current = true;
      setCaretOffset(el, restored.offset);
      if (restored.text !== valueRef.current) onCommitRef.current(readRef.current(el));
    } else {
      writeSubscripts(el, valueRef.current);
      lastSetRef.current = valueRef.current;
    }

    return () => {
      const pending = timerRef.current !== null;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (focusedRef.current && restoreKeyRef.current) {
        stash = {
          key: restoreKeyRef.current,
          text: readSubscripts(el),
          offset: getCaretOffset(el) ?? (el.textContent?.length ?? 0),
          createdAt: Date.now(),
        };
      }
      if (pending) {
        const text = readRef.current(el);
        if (text !== valueRef.current) onCommitRef.current(text);
      }
    };
  }, [editable]);

  // The value changed. Our own commit comes back as a value equal to what we
  // last set, and is ignored; anything else is an external change and is
  // written in, keeping the caret where it was if the field is focused.
  useLayoutEffect(() => {
    if (!editable) return;
    const el = elRef.current;
    if (!el) return;
    if (value === lastSetRef.current) return;
    const shown = readRef.current(el);
    if (shown === value) {
      lastSetRef.current = value;
      return;
    }
    if (focusedRef.current && shown !== lastSetRef.current) {
      // Keystrokes since the last commit live only in the DOM. Never
      // overwrite them: commit, and let the next value reconcile.
      commit();
      return;
    }
    const caret = focusedRef.current ? getCaretOffset(el) : null;
    writeSubscripts(el, value);
    lastSetRef.current = value;
    if (caret !== null) setCaretOffset(el, Math.min(caret, stripSubscripts(value).length));
  }, [value, editable, commit]);

  const handleInput = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(commit, COMMIT_DELAY_MS);
  }, [commit]);

  const handleFocus = useCallback((e: React.FocusEvent<HTMLElement>) => {
    focusedRef.current = true;
    onFocus?.(e);
  }, [onFocus]);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLElement>) => {
    focusedRef.current = false;
    commit();
    if (settleOnBlur) {
      const el = e.currentTarget;
      const text = readRef.current(el);
      // An emptied contentEditable keeps a <br>, which is not text but is
      // not :empty either, so the placeholder would stay hidden.
      if (readSubscripts(el) !== text || (text === '' && el.innerHTML !== '')) writeSubscripts(el, text);
    }
    onBlur?.(e);
  }, [commit, settleOnBlur, onBlur]);

  if (!editable) {
    return React.createElement(as, { key: 'static', ref: setEl, ...rest }, renderSubscripts(value));
  }
  return React.createElement(as, {
    key: 'editable',
    ref: setEl,
    contentEditable: true,
    suppressContentEditableWarning: true,
    ...rest,
    onInput: handleInput,
    onFocus: handleFocus,
    onBlur: handleBlur,
  });
};

export default LiveText;

/**
 * Tell a LiveText that its DOM was changed by script (a newline inserted at
 * the caret, a paste written by hand). Chrome fires no `input` event for a
 * programmatic edit, so without this the change would wait for the blur.
 */
export const notifyLiveTextInput = (el: HTMLElement): void => {
  el.dispatchEvent(new Event('input', { bubbles: true }));
};
